/**
 * Folder Manager - Create and manage folders for tag organization
 */
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FolderPlus, Folder, ChevronRight, MoreVertical, Edit2, Trash2, 
  X, Check, FolderOpen, Inbox, Package, ChevronDown
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL as API } from '../../config/api';

const FOLDER_COLORS = [
  { value: '#6366f1', label: 'Indigo' },
  { value: '#8b5cf6', label: 'Purple' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#ef4444', label: 'Red' },
  { value: '#f97316', label: 'Orange' },
  { value: '#eab308', label: 'Yellow' },
  { value: '#22c55e', label: 'Green' },
  { value: '#06b6d4', label: 'Cyan' },
  { value: '#3b82f6', label: 'Blue' },
  { value: '#64748b', label: 'Gray' }
];

export const FolderSidebar = ({ 
  folders, 
  selectedFolder, 
  onSelectFolder, 
  unorganizedCount,
  onFoldersChange,
  batches = [],
  batchCounts = {},
  onDragOver,
  onDrop,
  dragOverFolder
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [showBatches, setShowBatches] = useState(true);

  const handleCreateFolder = async (name, color) => {
    try {
      const response = await axios.post(`${API}/api/admin/folders`, { name, color });
      toast.success(`Folder "${name}" created`);
      onFoldersChange();
      setShowCreateModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create folder');
    }
  };

  const handleUpdateFolder = async (folderId, name, color) => {
    try {
      await axios.put(`${API}/api/admin/folders/${folderId}`, { name, color });
      toast.success('Folder updated');
      onFoldersChange();
      setEditingFolder(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update folder');
    }
  };

  const handleDeleteFolder = async (folderId, folderName) => {
    if (!window.confirm(`Delete folder "${folderName}"? Tags will be moved to unorganized.`)) return;
    
    try {
      await axios.delete(`${API}/api/admin/folders/${folderId}`);
      toast.success('Folder deleted');
      if (selectedFolder === folderId) {
        onSelectFolder(null);
      }
      onFoldersChange();
    } catch (error) {
      toast.error('Failed to delete folder');
    }
  };

  return (
    <div className="w-full bg-card rounded-xl p-3 border border-border/50 shadow-md">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Folders</h3>
        <button
          onClick={() => setShowCreateModal(true)}
          className="p-1.5 rounded-lg hover:bg-secondary transition-colors"
          title="Create folder"
          data-testid="create-folder-btn"
        >
          <FolderPlus className="w-4 h-4" />
        </button>
      </div>

      {/* Folder List */}
      <div className="space-y-1">
        {/* All Tags */}
        <button
          onClick={() => onSelectFolder(null)}
          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
            selectedFolder === null ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary'
          }`}
          data-testid="folder-all"
        >
          <Inbox className="w-4 h-4" />
          <span className="flex-1 text-left">All Tags</span>
        </button>

        {/* Unorganized */}
        <button
          onClick={() => onSelectFolder('unorganized')}
          onDragOver={(e) => onDragOver && onDragOver(e, null)}
          onDrop={(e) => onDrop && onDrop(e, null)}
          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
            selectedFolder === 'unorganized' ? 'bg-primary text-primary-foreground' : 
            dragOverFolder === null ? 'bg-blue-100 border-2 border-blue-400 border-dashed' : 'hover:bg-secondary'
          }`}
          data-testid="folder-unorganized"
        >
          <FolderOpen className="w-4 h-4 text-gray-400" />
          <span className="flex-1 text-left">Unorganized</span>
          <span className="text-xs bg-secondary px-1.5 py-0.5 rounded-full">{unorganizedCount}</span>
        </button>

        {/* Custom Folders */}
        {folders.map((folder) => (
          <div key={folder.folder_id} className="relative group">
            <button
              onClick={() => onSelectFolder(folder.folder_id)}
              onDragOver={(e) => onDragOver && onDragOver(e, folder.folder_id)}
              onDrop={(e) => onDrop && onDrop(e, folder.folder_id)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                selectedFolder === folder.folder_id ? 'bg-primary text-primary-foreground' : 
                dragOverFolder === folder.folder_id ? 'bg-blue-100 border-2 border-blue-400 border-dashed' : 'hover:bg-secondary'
              }`}
              data-testid={`folder-${folder.folder_id}`}
            >
              <Folder className="w-4 h-4" style={{ color: folder.color }} />
              <span className="flex-1 text-left truncate">{folder.name}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                selectedFolder === folder.folder_id ? 'bg-primary-foreground/20' : 'bg-secondary'
              }`}>
                {folder.tag_count}
              </span>
            </button>
            
            {/* Menu Button */}
            <button
              onClick={(e) => { e.stopPropagation(); setMenuOpen(menuOpen === folder.folder_id ? null : folder.folder_id); }}
              className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-black/10 transition-opacity"
            >
              <MoreVertical className="w-3.5 h-3.5" />
            </button>

            {/* Dropdown Menu */}
            <AnimatePresence>
              {menuOpen === folder.folder_id && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="absolute right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-lg z-20 py-1 min-w-[120px]"
                >
                  <button
                    onClick={() => { setEditingFolder(folder); setMenuOpen(null); }}
                    className="w-full px-3 py-1.5 text-left text-sm hover:bg-secondary flex items-center gap-2"
                  >
                    <Edit2 className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button
                    onClick={() => { handleDeleteFolder(folder.folder_id, folder.name); setMenuOpen(null); }}
                    className="w-full px-3 py-1.5 text-left text-sm hover:bg-secondary text-red-600 flex items-center gap-2"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>

      {/* Batches Section */}
      {batches && batches.length > 0 && (
        <div className="mt-4 pt-3 border-t border-border/50">
          <button
            onClick={() => setShowBatches(!showBatches)}
            className="w-full flex items-center justify-between mb-2 px-1"
          >
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Batches</h3>
            <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${showBatches ? '' : '-rotate-90'}`} />
          </button>
          
          {showBatches && (
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {batches.map((batchName) => (
                <button
                  key={batchName}
                  onClick={() => onSelectFolder(`batch:${batchName}`)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                    selectedFolder === `batch:${batchName}` ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary'
                  }`}
                  data-testid={`batch-${batchName}`}
                >
                  <Package className="w-4 h-4 text-blue-500" />
                  <span className="flex-1 text-left truncate">{batchName}</span>
                  {batchCounts[batchName] !== undefined && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      selectedFolder === `batch:${batchName}` ? 'bg-primary-foreground/20' : 'bg-secondary'
                    }`}>
                      {batchCounts[batchName]}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      <FolderModal
        isOpen={showCreateModal || editingFolder !== null}
        onClose={() => { setShowCreateModal(false); setEditingFolder(null); }}
        folder={editingFolder}
        onSave={editingFolder ? 
          (name, color) => handleUpdateFolder(editingFolder.folder_id, name, color) :
          handleCreateFolder
        }
      />
    </div>
  );
};


const FolderModal = ({ isOpen, onClose, folder, onSave }) => {
  const [name, setName] = useState(folder?.name || '');
  const [color, setColor] = useState(folder?.color || '#6366f1');
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (folder) {
      setName(folder.name);
      setColor(folder.color);
    } else {
      setName('');
      setColor('#6366f1');
    }
  }, [folder, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Please enter a folder name');
      return;
    }
    setSaving(true);
    await onSave(name.trim(), color);
    setSaving(false);
  };

  if (!isOpen) return null;

  // Use Portal to render modal at document root, escaping any parent z-index stacking contexts
  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
        style={{ zIndex: 9999 }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.95 }}
          className="bg-card rounded-2xl shadow-xl w-full max-w-sm"
          style={{ zIndex: 10000 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-5 border-b border-border flex items-center justify-between">
            <h3 className="font-semibold">{folder ? 'Edit Folder' : 'Create Folder'}</h3>
            <button onClick={onClose} className="p-1 hover:bg-secondary rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            {/* Folder Name */}
            <div>
              <label className="block text-sm font-medium mb-2">Folder Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Wedding Gifts"
                className="w-full px-4 py-3 bg-secondary rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                autoFocus
                data-testid="folder-name-input"
              />
            </div>

            {/* Color Picker */}
            <div>
              <label className="block text-sm font-medium mb-2">Color</label>
              <div className="flex flex-wrap gap-2">
                {FOLDER_COLORS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setColor(c.value)}
                    className={`w-8 h-8 rounded-lg transition-all ${
                      color === c.value ? 'ring-2 ring-offset-2 ring-primary scale-110' : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: c.value }}
                    title={c.label}
                  />
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="flex items-center gap-2 p-3 bg-secondary/50 rounded-xl">
              <Folder className="w-5 h-5" style={{ color }} />
              <span className="text-sm font-medium">{name || 'Folder Preview'}</span>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2.5 bg-secondary rounded-xl text-sm font-medium hover:bg-secondary/80 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || !name.trim()}
                className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="save-folder-btn"
              >
                {saving ? 'Saving...' : <><Check className="w-4 h-4" /> {folder ? 'Update' : 'Create'}</>}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};


export const MoveToFolderMenu = ({ isOpen, onClose, folders, onMove, position }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="fixed bg-card border border-border rounded-xl shadow-xl z-50 py-2 min-w-[180px]"
        style={{ top: position.top, left: position.left }}
      >
        <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase">Move to folder</div>
        
        {/* Unorganized option */}
        <button
          onClick={() => { onMove(null); onClose(); }}
          className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
        >
          <FolderOpen className="w-4 h-4 text-gray-400" />
          Unorganized
        </button>
        
        {folders.length > 0 && <div className="border-t border-border my-1" />}
        
        {folders.map((folder) => (
          <button
            key={folder.folder_id}
            onClick={() => { onMove(folder.folder_id); onClose(); }}
            className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
          >
            <Folder className="w-4 h-4" style={{ color: folder.color }} />
            {folder.name}
          </button>
        ))}
      </motion.div>
    </AnimatePresence>
  );
};

export default FolderSidebar;

// Export FolderModal for use in other components
export { FolderModal, FOLDER_COLORS };
